import { Color, Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { camelCase, omit, startCase } from 'lodash/fp';
import { useMemo, VFC } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import FieldSchemaSelect from 'dashboard/components/FieldSchemaSelect';
import { Option } from 'dashboard/components/JsonSchemaSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { UsableObjectConfig } from 'dashboard/lib/workflows';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const FieldMapping = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: 33% auto 5%;
  column-gap: ${Spacing[2]};
`;

interface FieldMappingsPanelProps {
  title: string;
  usableObjects: UsableObjectConfig[];
  fieldMappings: Record<string, string>;
  allowedFieldPaths?: string[];
  disallowedFieldPaths?: string[];
  removable?: boolean;
  presetFields?: Record<string, string>;
  onOutsideClick: () => void;
  onUpdateFieldMappings: (value: Record<string, string>) => void;
}

const REACT_SELECT_PORTAL_ID_PREFIX = 'field-mappings-panel__json-schema-select-';

/**
 * Renders a panel to update key-value object with JsonSchemaSelect per trigger type
 * @param {string} title - A text on the top of a panel
 * @param {UsableObjectConfig[]} usableObjects
 * @param {Record<string, string>} fieldMappings - key and value object to express current selection
 * @param {[WorkflowFieldObjectKey]} permittedObjects - types of objects to show criteria for
 * @param {[WorkflowStep]} activeStep - (optional) active/focused step
 * @param {[Record<string, string>]} presetFields - (optional) inputtable fields' key and label string. A whole `fieldMappings` is used instead if not given
 * @param {boolean} removable - (optional) whether key is removable (default: true)
 * @param {(value: Record<string, string>) => void} onUpdateFieldMappings - A handler to update current fieldMappings
 */
const FieldMappingsPanel: VFC<FieldMappingsPanelProps> = ({
  title,
  usableObjects,
  fieldMappings,
  allowedFieldPaths = [],
  disallowedFieldPaths = [],
  presetFields,
  removable = true,
  onOutsideClick,
  onUpdateFieldMappings,
}) => {
  const onFieldSelect = (field: string, option: Option) => {
    onUpdateFieldMappings({ ...fieldMappings, [field]: option.value });
  };

  const removeField = (field: string) => {
    onUpdateFieldMappings(omit(field, fieldMappings));
  };

  const fields = useMemo(() => {
    if (presetFields) {
      return Object.entries(presetFields).map(([key, label]) => [key, label]);
    }
    return Object.keys(fieldMappings).map((key) => [key, key]);
  }, [presetFields, fieldMappings]);

  return (
    <MaximizablePanel
      title={title}
      outsideExceptionalIdPrefixes={[REACT_SELECT_PORTAL_ID_PREFIX]}
      onOutsideClickAndEscPress={onOutsideClick}
    >
      {fields
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, label]) => (
          <div key={`${key}-${fieldMappings[key]?.replace('.', '-')}`}>
            <Spacer height={1} />
            <Well>
              <FieldMapping>
                <label htmlFor={`field-mappings-panel__json-schema-select-${key}`}>
                  {startCase(camelCase(label))}
                </label>
                <FieldSchemaSelect
                  ariaLabel={startCase(camelCase(label))}
                  id={`${REACT_SELECT_PORTAL_ID_PREFIX}${key}`}
                  data-test={`field-mappings-panel__json-schema-select-${key}`}
                  initialValue={fieldMappings[key] ?? ''}
                  usableObjects={usableObjects}
                  allowedFieldPaths={allowedFieldPaths}
                  disallowedFieldPaths={disallowedFieldPaths}
                  onFieldSelect={(option) => onFieldSelect(key, option)}
                />
                {removable && (
                  <Button
                    id={`field-mappings-panel__delete-field-${key}`}
                    type='button'
                    variant='basic'
                    width='icon'
                    aria-label={`Delete ${key}`}
                    onClick={() => removeField(key)}
                    data-test={`field-mappings-panel__delete-field-${key}`}
                  >
                    <TrashIcon />
                  </Button>
                )}
              </FieldMapping>
            </Well>
          </div>
        ))}
    </MaximizablePanel>
  );
};

export default FieldMappingsPanel;
